import { getContentHeight } from 'components/sections/Navigation/utils';
import useScreen from 'hooks/useScreen';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSettings, { IWebsiteConfigs } from 'store/settings';
import useSport from 'store/sport';
import MobileFooter from './MobileFooter';
import { useSearchParams } from 'react-router-dom';
import useWindowResize from 'hooks/useWindowResize';
import SectionLoader from 'components/sections/SectionLoader/SectionLoader';

declare global {
  interface Window {
    BetSdk;
  }
}

const TurboStars = ({ sportId }: { sportId: string }) => {
  const [sportState] = useSport();
  const { isMobile } = useScreen();
  const frameRef = useRef<null | {
    iframe: HTMLIFrameElement;
    sendMessage: (data: { type: string; data: { route: string } }) => void;
  }>(null);
  const [height, setHeight] = useState<number>();
  const [searchParams] = useSearchParams();
  const path = searchParams.get('tab') || '';
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();

  useEffect(() => {
    if (!sportState.data[sportId]) return;

    const script = document.createElement('script');

    const { turboStarsStyles } = meta;

    // Step 1: Define the CSS content
    const cssContent = Object.keys(turboStarsStyles).reduce((acc, key) => {
      return `${acc}${key}: ${turboStarsStyles[key]} !important;`;
    }, '');

    const cssContentUnderRoot = `:root {${cssContent}}`;

    // Step 2: Encode the CSS content to base64
    const base64CSSContent = btoa(cssContentUnderRoot);

    // Step 3: Create the Data URL
    const cssDataURL = `data:text/css;base64,${base64CSSContent}`;

    script.onload = async () => {
      setHeight(getContentHeight());
      frameRef.current = await window.BetSdk.init({
        host: sportState.data[sportId].url,
        mainFrameUrl: `${window.location.origin}/sport/${sportId}`,
        lang: sportState.data[sportId].lang,
        cid: sportState.data[sportId].cid,
        token: sportState.data[sportId].token,
        containerId: 'turbo-stars-container',
        customStyles: cssDataURL,
      });

      frameRef.current.iframe.onload = () => {
        frameRef.current?.sendMessage({
          type: 'routeChange',
          data: {
            route: `/${path}`,
          },
        });
      };
    };
    script.src = `${sportState.data[sportId].url}/assets/sdk/init.js`;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [sportState.data[sportId]]);

  useEffect(() => {
    if (frameRef.current) {
      frameRef.current?.sendMessage({
        type: 'routeChange',
        data: {
          route: `/${path}`,
        },
      });
    }
  }, [path]);

  const handleWindowHeight = useCallback(() => {
    setHeight(getContentHeight());
  }, []);

  useWindowResize(handleWindowHeight);

  if (!sportState.data[sportId]) return <SectionLoader />;

  return (
    <>
      <div style={{ height }} id="turbo-stars-container" />
      {isMobile && <MobileFooter url={path} />}
    </>
  );
};

export default TurboStars;
